/**
 * PDF Generator V2 - Structure normalisée française
 * Compatible enveloppes à fenêtre DL (110x220mm)
 * 
 * Structure obligatoire :
 * 1. Zone expéditeur (haut gauche)
 * 2. Zone destinataire (position enveloppe à fenêtre : X=105mm, Y=35mm)
 * 3. Lieu et date
 * 4. Objet + référence
 * 5. Titre du document
 * 6. Corps du texte
 * 7. Formule de politesse
 * 8. Signature
 * 9. Mention de conformité
 */

import { jsPDF } from 'jspdf';

// ==========================================
// UTILITAIRES
// ==========================================

export const formatDateFr = (dateStr) => {
  if (!dateStr) return formatDateFr(new Date().toISOString());
  const date = new Date(dateStr);
  const months = ["janvier", "février", "mars", "avril", "mai", "juin", 
                  "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

// Construire l'adresse formatée depuis les champs structurés
const buildAddress = (ligne1, ligne2, codePostal, ville) => {
  const parts = [];
  if (ligne1) parts.push(ligne1);
  if (ligne2) parts.push(ligne2);
  if (codePostal || ville) parts.push(`${codePostal || ''} ${ville || ''}`.trim());
  return parts;
};

// ==========================================
// CLASSE PDF GENERATOR
// ==========================================

export class PDFGenerator {
  constructor() {
    this.doc = new jsPDF();
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.margin = 20;
    this.textWidth = this.pageWidth - 2 * this.margin;
    this.y = 20;
    this.lineHeight = 5;
  }

  checkPageBreak(neededSpace = 25) {
    if (this.y + neededSpace > this.pageHeight - 25) {
      this.doc.addPage();
      this.y = 20;
    }
  }

  /**
   * Zone EXPÉDITEUR - Haut gauche
   * Format: NOM Prénom, Adresse ligne par ligne
   */
  addSenderZone(data) {
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    
    // NOM Prénom
    const fullName = `${(data.nom || '').toUpperCase()} ${data.prenom || ''}`.trim();
    if (fullName) {
      this.doc.text(fullName, this.margin, this.y);
      this.y += 5;
    }
    
    this.doc.setFont('helvetica', 'normal');
    
    // Adresse structurée
    const addressLines = buildAddress(
      data.adresse_ligne1,
      data.adresse_ligne2,
      data.code_postal,
      data.ville
    );
    
    addressLines.forEach(line => {
      this.doc.text(line, this.margin, this.y);
      this.y += 5;
    });
    
    // Téléphone et email si présents
    if (data.telephone) {
      this.doc.text(`Tél : ${data.telephone}`, this.margin, this.y);
      this.y += 5;
    }
    if (data.email) {
      this.doc.text(`Email : ${data.email}`, this.margin, this.y);
      this.y += 5;
    }
  }

  /**
   * Zone DESTINATAIRE - Position enveloppe à fenêtre
   * Position: X = 105mm, Y = 35mm
   */
  addRecipientZone(data) {
    if (!data.dest_organisme && !data.dest_adresse_ligne1) return;
    
    const recipientX = 105;
    let recipientY = 35;
    
    this.doc.setFontSize(10);
    
    // Nom de l'organisme
    if (data.dest_organisme) {
      this.doc.setFont('helvetica', 'bold');
      const orgLines = this.doc.splitTextToSize(data.dest_organisme, 80);
      orgLines.forEach(line => {
        this.doc.text(line, recipientX, recipientY);
        recipientY += 5;
      });
    }
    
    this.doc.setFont('helvetica', 'normal');
    
    // Service
    if (data.dest_service) {
      this.doc.text(data.dest_service, recipientX, recipientY);
      recipientY += 5;
    }
    
    // Adresse structurée
    const addressLines = buildAddress(
      data.dest_adresse_ligne1,
      data.dest_adresse_ligne2,
      data.dest_code_postal,
      data.dest_ville
    );
    
    addressLines.forEach(line => {
      this.doc.text(line, recipientX, recipientY);
      recipientY += 5;
    });
    
    // Ajuster Y si nécessaire
    if (recipientY > this.y) {
      this.y = recipientY;
    }
    this.y += 10;
  }

  /**
   * Lieu et date - aligné à droite
   */
  addDateAndPlace(lieu, date) {
    this.y = Math.max(this.y, 70); // S'assurer qu'on est sous le bloc destinataire
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    
    const dateStr = formatDateFr(date);
    const text = lieu ? `${lieu}, le ${dateStr}` : `Le ${dateStr}`;
    
    this.doc.text(text, this.pageWidth - this.margin, this.y, { align: 'right' });
    this.y += 12;
  }

  /**
   * Objet et référence
   */
  addObjet(objet, reference) {
    if (!objet && !reference) return;
    
    this.doc.setFontSize(10);
    
    if (objet) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Objet : ', this.margin, this.y);
      this.doc.setFont('helvetica', 'normal');
      const objetText = this.doc.splitTextToSize(objet, this.textWidth - 15);
      this.doc.text(objetText, this.margin + 15, this.y);
      this.y += objetText.length * 5 + 2;
    }
    
    if (reference) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Réf. : ', this.margin, this.y);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(reference, this.margin + 12, this.y);
      this.y += 7;
    }
    
    this.y += 5;
  }

  /**
   * Titre du document
   */
  addTitle(title) {
    this.checkPageBreak(15);
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title.toUpperCase(), this.pageWidth / 2, this.y, { align: 'center' });
    this.y += 12;
  }

  /**
   * Paragraphe de texte
   */
  addParagraph(text, options = {}) {
    if (!text) return;
    
    this.checkPageBreak(15);
    this.doc.setFontSize(options.fontSize || 10);
    this.doc.setFont('helvetica', options.bold ? 'bold' : 'normal');
    
    const lines = this.doc.splitTextToSize(text, this.textWidth);
    lines.forEach(line => {
      this.checkPageBreak(6);
      this.doc.text(line, this.margin, this.y);
      this.y += this.lineHeight;
    });
    
    this.y += options.spacing || 3;
  }

  /**
   * Formule de politesse
   */
  addClosingFormula(formula) {
    this.checkPageBreak(15);
    this.y += 3;
    this.addParagraph(formula || "Je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées.");
  }

  /**
   * Zone signature
   */
  addSignature(name, options = {}) {
    this.checkPageBreak(35);
    this.y += 8;
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    
    const signatureX = options.rightAlign ? this.pageWidth - this.margin - 50 : this.margin;
    
    this.doc.text('Signature :', signatureX, this.y);
    this.y += 18;
    
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(name, signatureX, this.y);
  }

  /**
   * Pied de page - Mention de conformité
   */
  addFooter() {
    const footerY = this.pageHeight - 15;
    this.doc.setFontSize(7);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(128, 128, 128);
    this.doc.text('Document généré sur PDF Facile (pdf-facile.fr) - Modèle conforme aux usages administratifs français.', this.pageWidth / 2, footerY, { align: 'center' });
    this.doc.setTextColor(0, 0, 0);
  }

  save(filename) {
    this.addFooter();
    this.doc.save(filename);
  }
}

// ==========================================
// HELPER POUR NOM COMPLET
// ==========================================
const getFullName = (data) => {
  return `${data.prenom || ''} ${(data.nom || '').toUpperCase()}`.trim();
};

const getFullAddress = (data) => {
  return buildAddress(data.adresse_ligne1, data.adresse_ligne2, data.code_postal, data.ville).join(', ');
};

// ==========================================
// TEMPLATES PDF PAR DOCUMENT
// ==========================================

export const generateAttestationHonneur = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  pdf.addTitle("Attestation sur l'honneur");
  
  let intro = `Je soussigné(e) ${getFullName(data)}`;
  if (data.date_naissance) intro += `, né(e) le ${formatDateShort(data.date_naissance)}`;
  if (data.lieu_naissance) intro += ` à ${data.lieu_naissance}`;
  intro += `, demeurant au ${getFullAddress(data)},`;
  
  pdf.addParagraph(intro);
  pdf.addParagraph("atteste sur l'honneur :");
  
  if (data.objet_attestation) {
    pdf.addParagraph(data.objet_attestation, { spacing: 6 });
  }
  
  pdf.addParagraph("Je suis informé(e) qu'une fausse déclaration m'expose à des sanctions pénales (articles 441-1 et suivants du Code pénal).");
  pdf.addParagraph("Fait pour servir et valoir ce que de droit.");
  
  pdf.addSignature(getFullName(data));
  return pdf;
};

export const generateAttestationHebergement = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  pdf.addTitle("Attestation d'hébergement");
  
  let intro = `Je soussigné(e) ${getFullName(data)}`;
  if (data.hebergeur_date_naissance) intro += `, né(e) le ${formatDateShort(data.hebergeur_date_naissance)}`;
  if (data.hebergeur_lieu_naissance) intro += ` à ${data.hebergeur_lieu_naissance}`;
  intro += `, demeurant au ${getFullAddress(data)},`;
  
  pdf.addParagraph(intro);
  pdf.addParagraph("certifie sur l'honneur héberger à mon domicile :");
  
  let heberge = `${data.heberge_prenom || ''} ${(data.heberge_nom || '').toUpperCase()}`;
  if (data.heberge_date_naissance) heberge += `, né(e) le ${formatDateShort(data.heberge_date_naissance)}`;
  if (data.lien_parente) heberge += ` (${data.lien_parente})`;
  
  pdf.addParagraph(heberge, { bold: true });
  
  if (data.date_debut) {
    pdf.addParagraph(`Cette personne est hébergée à titre gratuit à mon domicile depuis le ${formatDateShort(data.date_debut)}.`);
  }
  
  pdf.addParagraph("Je certifie sur l'honneur l'exactitude des informations ci-dessus.");
  pdf.addParagraph("Je suis informé(e) qu'une fausse déclaration m'expose à des sanctions pénales.");
  pdf.addParagraph("Fait pour servir et valoir ce que de droit.");
  
  pdf.addSignature(getFullName(data));
  return pdf;
};

export const generateAttestationDomicile = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  pdf.addTitle("Attestation de domicile");
  
  let intro = `Je soussigné(e) ${getFullName(data)}`;
  if (data.date_naissance) intro += `, né(e) le ${formatDateShort(data.date_naissance)}`;
  if (data.lieu_naissance) intro += ` à ${data.lieu_naissance}`;
  intro += `,`;
  
  pdf.addParagraph(intro);
  pdf.addParagraph("atteste sur l'honneur résider à l'adresse suivante :");
  
  const addressLines = buildAddress(data.adresse_ligne1, data.adresse_ligne2, data.code_postal, data.ville);
  addressLines.forEach(line => pdf.addParagraph(line, { bold: true }));
  
  if (data.date_installation) {
    pdf.addParagraph(`Je déclare occuper ce logement depuis le ${formatDateShort(data.date_installation)}.`);
  }
  
  pdf.addParagraph("Je certifie sur l'honneur l'exactitude de ces informations.");
  pdf.addParagraph("Fait pour servir et valoir ce que de droit.");
  
  pdf.addSignature(getFullName(data));
  return pdf;
};

export const generateDeclarationSituation = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  pdf.addTitle("Déclaration de situation personnelle");
  
  let intro = `Je soussigné(e) ${getFullName(data)}`;
  if (data.date_naissance) intro += `, né(e) le ${formatDateShort(data.date_naissance)}`;
  if (data.lieu_naissance) intro += ` à ${data.lieu_naissance}`;
  intro += `, demeurant au ${getFullAddress(data)},`;
  
  pdf.addParagraph(intro);
  pdf.addParagraph("déclare sur l'honneur être dans la situation suivante :");
  
  if (data.situation) {
    pdf.addParagraph(data.situation, { spacing: 6 });
  }
  
  pdf.addParagraph("Je certifie sur l'honneur l'exactitude de ces informations et m'engage à signaler tout changement de situation.");
  pdf.addParagraph("Fait pour servir et valoir ce que de droit.");
  
  pdf.addSignature(getFullName(data));
  return pdf;
};

export const generateAttestationSimple = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  pdf.addTitle("Attestation");
  
  pdf.addParagraph(`Je soussigné(e) ${getFullName(data)}, demeurant au ${getFullAddress(data)},`);
  pdf.addParagraph("atteste sur l'honneur ce qui suit :");
  
  if (data.contenu) {
    pdf.addParagraph(data.contenu, { spacing: 6 });
  }
  
  pdf.addParagraph("Fait pour servir et valoir ce que de droit.");
  
  pdf.addSignature(getFullName(data));
  return pdf;
};

// RÉSILIATIONS

export const generateResiliationAssurance = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(`Résiliation du contrat d'assurance ${data.type_assurance || ''}`, data.numero_contrat);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Je vous informe par la présente de ma décision de résilier le contrat d'assurance ${data.type_assurance || ''} n° ${data.numero_contrat || ''}, souscrit auprès de ${data.dest_organisme || ''}.`);
  
  if (data.motif) {
    pdf.addParagraph(`Motif de la résiliation : ${data.motif}.`);
  }
  
  if (data.date_effet) {
    pdf.addParagraph(`Je souhaite que cette résiliation prenne effet à compter du ${formatDateFr(data.date_effet)}.`);
  }
  
  pdf.addParagraph("Conformément aux dispositions légales (article L113-12 du Code des assurances), je vous demande de bien vouloir m'adresser une attestation de résiliation et de procéder au remboursement de la prime non consommée.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateResiliationInternet = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(`Résiliation d'abonnement ${data.type_abonnement || ''}`, data.numero_client);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Par la présente, je vous notifie ma volonté de résilier mon abonnement ${data.type_abonnement || ''} souscrit auprès de ${data.dest_organisme || ''}.`);
  
  pdf.addParagraph(`Numéro client : ${data.numero_client || ''}.`);
  
  if (data.motif) {
    pdf.addParagraph(`Motif de la résiliation : ${data.motif}.`);
  }
  
  if (data.date_effet) {
    pdf.addParagraph(`Je souhaite que cette résiliation soit effective à compter du ${formatDateFr(data.date_effet)}.`);
  }
  
  pdf.addParagraph("Je vous prie de bien vouloir me confirmer la prise en compte de cette demande et de m'indiquer les éventuelles démarches à suivre (restitution de matériel, solde de tout compte).");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateResiliationEnergie = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(`Résiliation de contrat ${data.type_energie || ''}`, data.numero_client);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Je vous informe de ma volonté de résilier mon contrat de fourniture d'${(data.type_energie || 'énergie').toLowerCase()} souscrit auprès de ${data.dest_organisme || ''}.`);
  
  pdf.addParagraph(`Numéro client / PDL : ${data.numero_client || ''}.`);
  pdf.addParagraph(`Adresse de fourniture : ${getFullAddress(data)}.`);
  
  if (data.releve_compteur) {
    pdf.addParagraph(`Relevé de compteur : ${data.releve_compteur}.`);
  }
  
  if (data.motif) {
    pdf.addParagraph(`Motif de la résiliation : ${data.motif}.`);
  }
  
  if (data.date_effet) {
    pdf.addParagraph(`Je souhaite que cette résiliation prenne effet le ${formatDateFr(data.date_effet)}.`);
  }
  
  pdf.addParagraph("Je vous prie de m'adresser la facture de régularisation à l'adresse mentionnée ci-dessus.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateResiliationSalleSport = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet("Résiliation d'abonnement", data.numero_membre);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Par la présente, je vous informe de ma décision de résilier mon abonnement auprès de ${data.dest_organisme || ''}.`);
  
  pdf.addParagraph(`Numéro de membre : ${data.numero_membre || ''}.`);
  
  if (data.motif) {
    pdf.addParagraph(`Motif de la résiliation : ${data.motif}.`);
  }
  
  if (data.date_effet) {
    pdf.addParagraph(`Je souhaite que cette résiliation soit effective à compter du ${formatDateFr(data.date_effet)}.`);
  }
  
  pdf.addParagraph("Conformément aux conditions générales et au Code de la consommation, je vous demande de prendre en compte cette demande et d'arrêter les prélèvements automatiques.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generatePreavisLogement = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet("Congé du locataire - Préavis de départ");
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Par la présente, je vous notifie mon intention de quitter le logement situé au ${getFullAddress(data)}, dont je suis actuellement locataire.`);
  
  if (data.type_bail) {
    const preavisDuree = data.type_bail.includes("1 mois") ? "un mois" : "trois mois";
    pdf.addParagraph(`Conformément à mon bail (${data.type_bail}), le délai de préavis applicable est de ${preavisDuree}.`);
  }
  
  if (data.date_depart) {
    pdf.addParagraph(`Je souhaite que mon départ soit effectif à compter du ${formatDateFr(data.date_depart)}.`);
  }
  
  pdf.addParagraph("Je me tiens à votre disposition pour convenir d'une date d'état des lieux de sortie.");
  
  if (data.nouvelle_adresse_ligne1) {
    const nouvelleAdresse = buildAddress(data.nouvelle_adresse_ligne1, data.nouvelle_adresse_ligne2, data.nouvelle_code_postal, data.nouvelle_ville).join(', ');
    pdf.addParagraph(`Je vous prie d'adresser le remboursement du dépôt de garantie à : ${nouvelleAdresse}.`);
  }
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

// RÉCLAMATIONS

export const generateLettreReclamation = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(data.objet, data.reference);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Je me permets de vous adresser cette réclamation concernant ${(data.objet || '').toLowerCase()}.`);
  
  if (data.description) {
    pdf.addParagraph("Description du problème :", { bold: true });
    pdf.addParagraph(data.description);
  }
  
  if (data.demande) {
    pdf.addParagraph("En conséquence, je vous demande :", { bold: true });
    pdf.addParagraph(data.demande);
  }
  
  pdf.addParagraph("Sans réponse satisfaisante de votre part dans un délai raisonnable, je me réserve le droit de saisir les instances compétentes.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateDemandeRemboursement = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(`Demande de remboursement - ${data.montant || ''}`, data.reference);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Par la présente, je sollicite le remboursement d'un montant de ${data.montant || ''}.`);
  
  if (data.reference) {
    pdf.addParagraph(`Référence : ${data.reference}.`);
  }
  
  if (data.motif) {
    pdf.addParagraph("Motif de la demande :", { bold: true });
    pdf.addParagraph(data.motif);
  }
  
  if (data.iban) {
    pdf.addParagraph(`Je vous prie de procéder au virement sur le compte : ${data.iban}.`);
  }
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateContestation = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(`Contestation - ${data.type_contestation || ''}`, data.reference);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Par la présente, je conteste formellement ${(data.type_contestation || '').toLowerCase()} portant la référence ${data.reference || ''}.`);
  
  if (data.arguments) {
    pdf.addParagraph("Motifs de ma contestation :", { bold: true });
    pdf.addParagraph(data.arguments);
  }
  
  if (data.demande) {
    pdf.addParagraph("En conséquence, je demande :", { bold: true });
    pdf.addParagraph(data.demande);
  }
  
  pdf.addParagraph("Je me réserve le droit de saisir les juridictions compétentes si cette contestation n'était pas prise en compte.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateMiseEnDemeure = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addTitle("Mise en demeure");
  pdf.addObjet(data.objet);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  if (data.description) {
    pdf.addParagraph(data.description);
  }
  
  if (data.montant) {
    pdf.addParagraph(`Montant dû : ${data.montant}.`, { bold: true });
  }
  
  pdf.addParagraph(`Par la présente, je vous mets en demeure de régulariser cette situation dans un délai de ${data.delai || '8'} jours à compter de la réception de ce courrier.`, { bold: true });
  
  pdf.addParagraph("À défaut, je me verrai contraint(e) d'engager toute procédure utile (recouvrement, action en justice), sans autre mise en demeure préalable.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateLettreExplicative = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(data.objet, data.reference);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph("Par la présente, je souhaite vous apporter les explications suivantes :");
  
  if (data.explication) {
    pdf.addParagraph(data.explication, { spacing: 6 });
  }
  
  pdf.addParagraph("Je reste à votre disposition pour tout renseignement complémentaire.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

// LOGEMENT

export const generateLettreBailleur = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(data.type_demande || "Demande");
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Locataire du logement situé au ${getFullAddress(data)}, je me permets de vous contacter.`);
  
  if (data.type_demande) {
    pdf.addParagraph(`Type de demande : ${data.type_demande}.`, { bold: true });
  }
  
  if (data.description) {
    pdf.addParagraph(data.description, { spacing: 6 });
  }
  
  pdf.addParagraph("Je reste à votre disposition pour tout renseignement complémentaire.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateLettreSyndic = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(data.objet);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`En ma qualité de copropriétaire/occupant du logement situé au ${getFullAddress(data)}, je me permets de vous adresser cette demande.`);
  
  if (data.description) {
    pdf.addParagraph(data.description, { spacing: 6 });
  }
  
  pdf.addParagraph("Je vous remercie de bien vouloir prendre en considération cette demande.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateDemandeLogement = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(`Demande de logement - ${data.type_logement || ''}`);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph("Par la présente, je sollicite un logement auprès de votre organisme.");
  
  if (data.situation) {
    pdf.addParagraph("Ma situation actuelle :", { bold: true });
    pdf.addParagraph(data.situation);
  }
  
  if (data.type_logement) {
    pdf.addParagraph(`Type de logement souhaité : ${data.type_logement}.`);
  }
  
  if (data.motivation) {
    pdf.addParagraph("Motivations :", { bold: true });
    pdf.addParagraph(data.motivation);
  }
  
  pdf.addParagraph("Je reste à votre entière disposition pour fournir tout document complémentaire.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateLettreMairie = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(data.objet);
  
  const destinataire = data.dest_service || "Madame, Monsieur";
  pdf.addParagraph(`${destinataire},`);
  
  pdf.addParagraph("Par la présente, je me permets de solliciter votre attention concernant la demande suivante :");
  
  if (data.contenu) {
    pdf.addParagraph(data.contenu, { spacing: 6 });
  }
  
  pdf.addParagraph("Je reste à votre disposition pour tout renseignement complémentaire.");
  
  pdf.addClosingFormula(`Je vous prie d'agréer, ${destinataire}, l'expression de ma considération distinguée.`);
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

// TRAVAIL

export const generateLettreEmployeur = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(data.type_demande || "Demande");
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Employé(e) au sein de ${data.dest_organisme || ''} en qualité de ${data.poste || ''}, je me permets de vous adresser cette demande.`);
  
  if (data.type_demande) {
    pdf.addParagraph(`Objet : ${data.type_demande}.`, { bold: true });
  }
  
  if (data.dates) {
    pdf.addParagraph(`Dates concernées : ${data.dates}.`);
  }
  
  if (data.description) {
    pdf.addParagraph(data.description, { spacing: 6 });
  }
  
  pdf.addParagraph("Je reste à votre disposition pour tout renseignement complémentaire.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateDemandeExceptionnelle = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(data.objet);
  
  const destinataire = data.dest_service || "Madame, Monsieur";
  pdf.addParagraph(`${destinataire},`);
  
  pdf.addParagraph("Par la présente, je souhaite vous soumettre une demande exceptionnelle.");
  
  if (data.description) {
    pdf.addParagraph(data.description, { spacing: 6 });
  }
  
  pdf.addParagraph("Je reste à votre disposition pour échanger à ce sujet.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateLettreMotivation = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(`Candidature au poste de ${data.poste || ''}`);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Actuellement à la recherche d'une nouvelle opportunité professionnelle, je vous adresse ma candidature pour le poste de ${data.poste || ''} au sein de ${data.dest_organisme || ''}.`);
  
  if (data.parcours) {
    pdf.addParagraph("Mon parcours :", { bold: true });
    pdf.addParagraph(data.parcours);
  }
  
  if (data.competences) {
    pdf.addParagraph("Mes compétences clés :", { bold: true });
    pdf.addParagraph(data.competences);
  }
  
  if (data.motivation) {
    pdf.addParagraph("Mes motivations :", { bold: true });
    pdf.addParagraph(data.motivation);
  }
  
  pdf.addParagraph("Je serais ravi(e) de vous exposer mon parcours lors d'un entretien.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateDemandeStage = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(`Demande de stage - ${data.domaine || ''}`);
  
  pdf.addParagraph("Madame, Monsieur,");
  
  pdf.addParagraph(`Actuellement étudiant(e) en ${data.formation || ''} au sein de ${data.etablissement || ''}, je suis à la recherche d'un stage dans le domaine de ${data.domaine || ''}.`);
  
  if (data.duree) pdf.addParagraph(`Durée souhaitée : ${data.duree}.`);
  if (data.dates) pdf.addParagraph(`Période souhaitée : ${data.dates}.`);
  
  if (data.motivation) {
    pdf.addParagraph("Motivations :", { bold: true });
    pdf.addParagraph(data.motivation);
  }
  
  pdf.addParagraph("Ce stage représente une opportunité de mettre en pratique mes connaissances théoriques.");
  pdf.addParagraph("Je reste à votre disposition pour un entretien.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

export const generateLettreEtablissement = (data) => {
  const pdf = new PDFGenerator();
  
  pdf.addSenderZone(data);
  pdf.addRecipientZone(data);
  pdf.addDateAndPlace(data.lieu_signature, data.date_document);
  
  pdf.addObjet(data.objet);
  
  const destinataire = data.dest_service || "Madame, Monsieur";
  pdf.addParagraph(`${destinataire},`);
  
  pdf.addParagraph(`Parent/Responsable légal de ${data.prenom_eleve || ''} ${(data.nom_eleve || '').toUpperCase()}, élève en classe de ${data.classe || ''}, je me permets de vous contacter.`);
  
  if (data.contenu) {
    pdf.addParagraph(data.contenu, { spacing: 6 });
  }
  
  pdf.addParagraph("Je reste à votre disposition pour tout renseignement complémentaire.");
  
  pdf.addClosingFormula();
  pdf.addSignature(getFullName(data), { rightAlign: true });
  return pdf;
};

// ==========================================
// MAPPING ET EXPORT
// ==========================================

const generators = {
  'attestation-honneur': generateAttestationHonneur,
  'attestation-hebergement': generateAttestationHebergement,
  'attestation-domicile': generateAttestationDomicile,
  'declaration-situation': generateDeclarationSituation,
  'attestation-simple': generateAttestationSimple,
  'resiliation-assurance': generateResiliationAssurance,
  'resiliation-internet': generateResiliationInternet,
  'resiliation-energie': generateResiliationEnergie,
  'resiliation-salle-sport': generateResiliationSalleSport,
  'preavis-logement': generatePreavisLogement,
  'lettre-reclamation': generateLettreReclamation,
  'demande-remboursement': generateDemandeRemboursement,
  'contestation': generateContestation,
  'mise-en-demeure': generateMiseEnDemeure,
  'lettre-explicative': generateLettreExplicative,
  'lettre-bailleur': generateLettreBailleur,
  'lettre-syndic': generateLettreSyndic,
  'demande-logement': generateDemandeLogement,
  'lettre-mairie': generateLettreMairie,
  'lettre-employeur': generateLettreEmployeur,
  'demande-exceptionnelle': generateDemandeExceptionnelle,
  'lettre-motivation': generateLettreMotivation,
  'demande-stage': generateDemandeStage,
  'lettre-etablissement': generateLettreEtablissement
};

export const generateAndDownloadPDF = (documentId, documentName, formData) => {
  const generator = generators[documentId];
  
  if (!generator) {
    console.error(`No generator found for document: ${documentId}`);
    const pdf = generateAttestationSimple({ ...formData, contenu: formData.description || formData.contenu || '' });
    const fileName = `${documentName?.toLowerCase().replace(/[^a-z0-9]/g, '_') || 'document'}_${Date.now()}.pdf`;
    pdf.save(fileName);
    return;
  }
  
  const pdf = generator(formData);
  const fileName = `${documentName?.toLowerCase().replace(/[^a-z0-9]/g, '_') || 'document'}_${Date.now()}.pdf`;
  pdf.save(fileName);
};
